import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import FirstLoginDisclaimer from "components/FirstLoginDisclaimer";
import InstallBanner from "components/InstallBanner";

// Pages
import NotFound from "pages/NotFound";
import SubscriptionPlans from './pages/subscription-plans';
import MainDashboard from './pages/main-dashboard';
import Login from './pages/login';
import AccountSettings from './pages/account-settings';
import ReflectionHistory from './pages/reflection-history';
import ReflectionResponse from './pages/reflection-response';
import Register from './pages/register';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import PaymentSuccess from './pages/payment-success';
import AdminManagement from './pages/admin-management';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-dvh bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Protected Route Component - requires authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  return children;
};

// HARD GATE Route - requires authentication + valid subscription (or admin)
// Non-subscribers are redirected to subscription-plans
const SubscriberRoute = ({ children }) => {
  const { isAuthenticated, loading, canAccessApp, isAdmin } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  // ADMIN BYPASS: Admins always have access
  if (isAdmin) return children;
  
  // HARD GATE: Non-subscribers must subscribe first
  if (!canAccessApp) {
    return <Navigate to="/subscription-plans" replace />;
  }
  
  return children;
};

// ADMIN ONLY Route - requires authentication + isAdmin === true
// Non-admins get "Access Denied" - cannot bypass
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  // STRICT ADMIN CHECK: Only isAdmin === true can access
  if (!isAdmin) {
    return <Navigate to="/main-dashboard" replace />;
  }
  
  return children;
};

// Public Route - redirects to dashboard if already logged in AND has valid subscription
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, canAccessApp, isAdmin } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  
  if (isAuthenticated) {
    // ADMIN BYPASS or valid subscriber -> go to dashboard
    if (isAdmin || canAccessApp) {
      return <Navigate to="/main-dashboard" replace />;
    }
    // Authenticated but no subscription -> go to payment
    return <Navigate to="/subscription-plans" replace />;
  }
  
  return children;
};

const Routes = () => {
  const { showFirstLoginDisclaimer, dismissDisclaimer } = useAuth();

  // Smart Landing - New users go to Register, Returning users go to Login
  const SmartLanding = () => {
    const { isAuthenticated, loading, canAccessApp, isAdmin } = useAuth();
    
    if (loading) return <LoadingSpinner />;
    
    // If already authenticated, redirect appropriately
    if (isAuthenticated) {
      if (isAdmin || canAccessApp) {
        return <Navigate to="/main-dashboard" replace />;
      }
      return <Navigate to="/subscription-plans" replace />;
    }
    
    // Check if returning user (has visited before or has auth token stored)
    const hasVisitedBefore = localStorage.getItem('lumiveta_has_visited') === 'true';
    const hasStoredToken = localStorage.getItem('lumiveta_token');
    
    if (hasVisitedBefore || hasStoredToken) {
      // Returning user -> Login page
      return <Navigate to="/login" replace />;
    } else {
      // New user -> Register page (see free trial)
      // Mark as visited for future
      localStorage.setItem('lumiveta_has_visited', 'true');
      return <Navigate to="/register" replace />;
    }
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        {/* Smart Install Banner - shows on mobile browsers */}
        <InstallBanner />
        <FirstLoginDisclaimer 
          isVisible={showFirstLoginDisclaimer} 
          onClose={dismissDisclaimer} 
        />
        <RouterRoutes>
          {/* Smart Landing - Routes new vs returning users */}
          <Route path="/" element={<SmartLanding />} />
          
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          
          {/* Subscription Page - Protected but accessible without subscription */}
          <Route path="/subscription-plans" element={
            <ProtectedRoute><SubscriptionPlans /></ProtectedRoute>
          } />
          <Route path="/payment-success" element={
            <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
          } />
          
          {/* HARD GATE Routes - require subscription OR admin */}
          <Route path="/main-dashboard" element={
            <ProtectedRoute><MainDashboard /></ProtectedRoute>
          } />
          <Route path="/account-settings" element={
            <SubscriberRoute><AccountSettings /></SubscriberRoute>
          } />
          <Route path="/history" element={
            <ProtectedRoute><ReflectionHistory /></ProtectedRoute>
          } />
          <Route path="/reflection-history" element={
            <ProtectedRoute><ReflectionHistory /></ProtectedRoute>
          } />
          <Route path="/reflection-response" element={
            <SubscriberRoute><ReflectionResponse /></SubscriberRoute>
          } />
          
          {/* Admin Management - ADMIN ONLY (strict isAdmin check) */}
          <Route path="/admin-management" element={
            <AdminRoute><AdminManagement /></AdminRoute>
          } />
          
          {/* Legacy /admin route alias */}
          <Route path="/admin" element={
            <AdminRoute><AdminManagement /></AdminRoute>
          } />
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
